'use client';

import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { Component, ProductType } from '@headless-workspace/typings';
import { PredicateHelpers } from '@headless-workspace/utils';
import {
    mapProductDetailInformationValue,
    ProductDetailInformationValue,
    ProductDetailValue,
    ProductDetailVariantValue,
    ProductSetItemValue,
} from '../../../../../src';
import { useProductDetailsInformation, useProductSetInfoMutation } from '../infos';
import { ProductDetailContext, ProductDetailContextType } from './ProductDetailContext';

type ProductDetailProviderProps = {
    initialProductDetailValue: ProductDetailValue;
} & PropsWithChildren;

export const getInitialProductSetVariants = (
    productsSetList: ProductSetItemValue[],
): Record<number, ProductDetailVariantValue | undefined> => {
    const productSetDefaultVariants: Record<number, ProductDetailVariantValue | undefined> = {};

    productsSetList.forEach((product, index) => {
        // Picks the default variant by priority: defaultVariantId > variant with same id as product > first variant
        const defaultVariant =
            product.variants.find((variant) => variant.id === product.defaultVariantId) ||
            product.variants.find((variant) => variant.id === product.id) ||
            product.variants[0];

        if (defaultVariant) {
            productSetDefaultVariants[index] = defaultVariant;
        }
    });
    return productSetDefaultVariants;
};

export const ProductDetailProvider: Component<ProductDetailProviderProps> = ({
    children,
    initialProductDetailValue,
}) => {
    const productSetList = initialProductDetailValue.productsSetList;
    const [selectedProductSetVariants, setSelectedProductSetVariants] = useState<
        Record<number, ProductDetailVariantValue | undefined>
    >(getInitialProductSetVariants(productSetList ?? []));

    const [updatedProductDetailsInformation, setUpdatedProductDetailsInformation] = useState<
        Partial<ProductDetailInformationValue>
    >({});

    const { getProductSetInfo, isGettingProductSetInfo } = useProductSetInfoMutation();

    const defaultProductDetailValue: ProductDetailInformationValue = useMemo(
        () => mapProductDetailInformationValue(initialProductDetailValue),
        [initialProductDetailValue],
    );

    const { asyncData, setSku } = useProductDetailsInformation(initialProductDetailValue);
    const productDetailsInformation = unwrapResultSuccess(asyncData.result);

    // Update the product variant details state to override some values (used in the product set for update the price)
    const updateProductDetails = useCallback((productDetails: Partial<ProductDetailValue>) => {
        setUpdatedProductDetailsInformation((prevState) => {
            return { ...prevState, ...productDetails };
        });
    }, []);

    const selectedVariantIds = useMemo<string[]>(() => {
        if (productDetailsInformation?.type === ProductType.PRODUCT_SET) {
            return Object.values(selectedProductSetVariants)
                .map((product) => product?.id)
                .filter(PredicateHelpers.notNullOrUndefined);
        }

        if (!productDetailsInformation) {
            return [];
        }

        return [productDetailsInformation.sku];
    }, [productDetailsInformation, selectedProductSetVariants]);

    const productsSetAvailability = useMemo(() => {
        return Object.values(selectedProductSetVariants).every((variant) => variant?.isAvailable);
    }, [selectedProductSetVariants]);

    const productDetailStateProviderValue: ProductDetailContextType = useMemo<ProductDetailContextType>(
        () => ({
            productDetails: productDetailsInformation
                ? ({
                      ...productDetailsInformation,
                      ...updatedProductDetailsInformation,
                  } as ProductDetailInformationValue)
                : defaultProductDetailValue,
            setProductVariantId: setSku,
            isLoading: isGettingProductSetInfo || asyncData.isLoading,
            updateProductDetails,
            selectedProductSetVariants,
            setSelectedProductSetVariants,
            productSetList,
            selectedVariantIds,
            productsSetAvailability,
            getProductSetInfo,
        }),
        [
            productDetailsInformation,
            isGettingProductSetInfo,
            asyncData.isLoading,
            updateProductDetails,
            setSku,
            updatedProductDetailsInformation,
            selectedProductSetVariants,
            setSelectedProductSetVariants,
            productSetList,
            defaultProductDetailValue,
            selectedVariantIds,
            productsSetAvailability,
            getProductSetInfo,
        ],
    );

    return (
        <ProductDetailContext.Provider value={productDetailStateProviderValue}>
            {children}
        </ProductDetailContext.Provider>
    );
};
